import { z } from 'zod'

import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  CostMarginPercentSchema,
  DescriptionSchema,
  DollarsSchema,
  NameSchema,
  NoteSchema,
} from './property.models.ts'
import {
  PreProductionReviewSchema,
  ReferenceFileRequiredSchema,
  ServiceKeySchema,
  ServicePayoutSchema,
  ServiceTranslationsSchema,
  ServiceTypeSchema,
} from './service.models.ts'
import { TagFilterSchema } from './tag.models.ts'
import {
  PackageIdSchema,
  PackageServiceIdSchema,
  ProjectIdSchema,
  ProjectServiceIdSchema,
  ProjectServiceUrnSchema,
  ServiceIdSchema,
} from './unique-id.models.ts'

/**
 * Count
 */
export const ProjectServiceCountSchema = z.int().min(1)
export type ProjectServiceCount = ZodInfer<typeof ProjectServiceCountSchema>

/**
 * ProjectServiceDomainSelectModel
 */
export const ProjectServiceDomainSelectModelSchema = z.object({
  projectServiceId: ProjectServiceIdSchema,
  projectServiceUrn: ProjectServiceUrnSchema,
  projectId: ProjectIdSchema,
  serviceId: ServiceIdSchema,
  packageId: PackageIdSchema,
  packageServiceId: PackageServiceIdSchema,
  name: NameSchema,
  description: DescriptionSchema,
  serviceType: ServiceTypeSchema,
  serviceKey: ServiceKeySchema.optional(),
  translations: ServiceTranslationsSchema.optional(),
  cost: ServicePayoutSchema.optional(),
  costPlusMargin: DollarsSchema().optional(),
  costMarginPercent: CostMarginPercentSchema,
  brandNote: NoteSchema.optional(),
  internalPricingNote: NoteSchema.optional(),
  count: ProjectServiceCountSchema,
  preProductionReview: PreProductionReviewSchema,
  preProductionBrandReferenceFileRequired: ReferenceFileRequiredSchema,
  preProductionMakerReferenceFileRequired: ReferenceFileRequiredSchema,
})

export type ProjectServiceDomainSelectModel = ZodInfer<
  typeof ProjectServiceDomainSelectModelSchema
>

/**
 * ProjectServiceDomainInsertModel
 */
export const ProjectServiceDomainInsertModelSchema =
  ProjectServiceDomainSelectModelSchema.omit({
    projectServiceUrn: true,
    costPlusMargin: true,
  })
    .partial({
      projectServiceId: true,
    })
    .extend({
      costMarginPercent: CostMarginPercentSchema,
      // Defaults only apply at insert time — base schemas are default-free
      // so that .partial() works correctly in update models.
      preProductionReview: PreProductionReviewSchema.prefault(false),
      preProductionBrandReferenceFileRequired:
        ReferenceFileRequiredSchema.prefault(false),
      preProductionMakerReferenceFileRequired:
        ReferenceFileRequiredSchema.prefault(false),
    })

export type ProjectServiceDomainInsertModel = ZodInfer<
  typeof ProjectServiceDomainInsertModelSchema
>

/**
 * ProjectServiceDomainUpdateModel
 */
export const ProjectServiceDomainUpdateModelSchema = z
  .object({
    brandNote: ProjectServiceDomainSelectModelSchema.shape.brandNote.nullable(),
    internalPricingNote:
      ProjectServiceDomainSelectModelSchema.shape.internalPricingNote.nullable(),
    cost: ProjectServiceDomainSelectModelSchema.shape.cost.nullable(),
  })
  .partial()

export type ProjectServiceDomainUpdateModel = ZodInfer<
  typeof ProjectServiceDomainUpdateModelSchema
>

/**
 * ProjectServiceDomainSelectModelFilters
 */
export const ProjectServiceDomainSelectModelFiltersSchema = z
  .object({
    projectServiceId: ModelFilterSchema(ProjectServiceIdSchema),
    projectServiceUrn: ModelFilterSchema(ProjectServiceUrnSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    serviceId: ModelFilterSchema(ServiceIdSchema),
    preProductionReview: PreProductionReviewSchema,
    tags: TagFilterSchema,
  })
  .partial()

export type ProjectServiceDomainSelectModelFilters = ZodInfer<
  typeof ProjectServiceDomainSelectModelFiltersSchema
>

export const [
  ProjectServiceCategories,
  ProjectServiceCategorySchema,
  ProjectServiceCategoryEnum,
] = EnumBuilder('service', 'deliverable')
export type ProjectServiceCategory = ZodInfer<
  typeof ProjectServiceCategorySchema
>
